import { useMemo } from "react";
import { hashSeed, makeRng } from "../../shared/rng";
import type { Family, ProgressRecord } from "../../shared/model";

// Your pond: every solved puzzle is a lily pad. It grows with how long you thought, and blooms with a lotus when you
// solved it without a single nudge. Positions come from the puzzle id, so the pond never rearranges itself.

const W = 800;
const H = 300;
const MAX_PADS = 70;

const PAD_FILL: Record<Family, [string, string]> = {
  cut: ["#79a86f", "#5f9257"],
  stars: ["#a3b562", "#86a04a"],
  coin: ["#63a89a", "#4a8f82"]
};

interface Pad {
  id: string;
  family: Family;
  x: number;
  y: number;
  r: number;
  rot: number;
  bloom: boolean;
  delay: number;
}

function layout(records: ProgressRecord[]): Pad[] {
  const placed: Pad[] = [];
  const list = records.slice(0, MAX_PADS);
  for (const rec of list) {
    const rng = makeRng("pad|" + rec.id);
    const r = 15 + Math.min(15, (rec.seconds / 60) * 1.6);
    let best = { x: 0, y: 0, score: -1 };
    for (let t = 0; t < 40; t++) {
      const x = rng.range(r + 14, W - r - 14);
      const y = rng.range(r + 46, H - r - 12);
      let score = Infinity;
      for (const o of placed) score = Math.min(score, Math.hypot(o.x - x, o.y - y) - (o.r + r));
      if (placed.length === 0) score = 1000;
      if (score > best.score) best = { x, y, score };
      if (score > 6) break; // a comfortable spot
    }
    placed.push({
      id: rec.id, family: rec.family, x: best.x, y: best.y, r, rot: rng.range(0, 360),
      bloom: rec.hints === 0 && !rec.result.includes("peek"), delay: rng.range(0, 6)
    });
  }
  return placed;
}

function padPath(r: number): string {
  const gap = 0.55;
  const a0 = gap / 2;
  const a1 = -gap / 2;
  const p0 = [r * Math.cos(a0), r * Math.sin(a0)];
  const p1 = [r * Math.cos(a1), r * Math.sin(a1)];
  return `M0 0 L${p0[0].toFixed(2)} ${p0[1].toFixed(2)} A${r} ${r} 0 1 1 ${p1[0].toFixed(2)} ${p1[1].toFixed(2)} Z`;
}

function Lotus({ r }: { r: number }) {
  const s = Math.max(0.75, r / 22);
  return (
    <g transform={`scale(${s})`}>
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <ellipse key={a} cx={0} cy={-6} rx={3.4} ry={7.5} transform={`rotate(${a})`} fill="#f7c2bd" stroke="#e58f97" strokeWidth={0.6} />
      ))}
      {[30, 90, 150, 210, 270, 330].map((a) => (
        <ellipse key={a} cx={0} cy={-4.5} rx={2.6} ry={5.5} transform={`rotate(${a})`} fill="#fde3df" stroke="#eba3a8" strokeWidth={0.5} />
      ))}
      <circle r={2.4} fill="#f3c84f" />
    </g>
  );
}

export function Pond({ solved, koi }: { solved: ProgressRecord[]; koi: number }) {
  const pads = useMemo(() => layout([...solved].sort((a, b) => (a.solvedAt ?? 0) - (b.solvedAt ?? 0))), [solved]);
  const blooms = pads.filter((p) => p.bloom).length;
  const seed = useMemo(() => hashSeed("pond"), []);
  return (
    <figure className="pond" aria-label={`Your pond: ${pads.length} lily pad${pads.length === 1 ? "" : "s"}, ${blooms} in bloom`}>
      <div className="pond-water">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="pondwater" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--pond-top)" />
            <stop offset="100%" stopColor="var(--pond-bottom)" />
          </linearGradient>
        </defs>
        <rect width={W} height={H} fill="url(#pondwater)" />
        {/* soft ripples of light */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <path key={i} d={`M ${-40 + i * 150} ${60 + ((seed >> i) % 6) * 38} q 40 -9 80 0 t 80 0`} className="pond-wave" />
        ))}
        {koi > 0 &&
          Array.from({ length: koi }, (_, i) => (
            <g key={i} transform={`translate(0 ${70 + i * 62})`}>
              <g className={"pond-koi k" + i}>
                <ellipse rx={15} ry={6} fill="#e9724c" />
                <path d="M-13 0 L-24 -6 L-22 0 L-24 6 Z" fill="#e9724c" />
                <ellipse cx={4} cy={-1} rx={5} ry={2.5} fill="#fbd9c9" opacity={0.9} />
                <circle cx={10} cy={-2} r={1.1} fill="#1b3a38" />
              </g>
            </g>
          ))}
        {pads.map((p) => (
          <g key={p.id} transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})`}>
            <g className="pond-bob" style={{ animationDelay: `${p.delay}s` }}>
              <ellipse cx={0} cy={3} rx={p.r + 2} ry={p.r * 0.35} fill="rgba(0,0,0,0.12)" />
              <g transform={`rotate(${p.rot})`}>
                <path d={padPath(p.r)} fill={PAD_FILL[p.family][0]} stroke={PAD_FILL[p.family][1]} strokeWidth={1.4} />
                <path d={`M0 0 L${p.r * 0.85} 0 M0 0 L${p.r * 0.5} ${p.r * 0.6} M0 0 L${p.r * 0.5} ${-p.r * 0.6} M0 0 L${-p.r * 0.7} ${p.r * 0.35} M0 0 L${-p.r * 0.7} ${-p.r * 0.35}`} stroke={PAD_FILL[p.family][1]} strokeWidth={0.8} opacity={0.55} fill="none" />
              </g>
              {p.bloom && <Lotus r={p.r} />}
            </g>
          </g>
        ))}
        {pads.length === 0 && (
          <g transform="translate(400 170)">
            <ellipse cx={0} cy={10} rx={46} ry={12} fill="rgba(0,0,0,0.14)" />
            <path d="M-40 6 Q-30 -26 6 -24 Q42 -22 40 6 Z" fill="#8a8d86" />
            <path d="M-26 -10 Q-8 -20 12 -14" stroke="#b7bab2" strokeWidth={3} fill="none" strokeLinecap="round" />
          </g>
        )}
      </svg>
      </div>
      <figcaption className="pond-cap">
        {pads.length === 0 ? (
          <span>Solve a puzzle and your first lily pad will appear here.</span>
        ) : (
          <span>
            <strong>{pads.length}</strong> lily pad{pads.length === 1 ? "" : "s"}
            {blooms > 0 && <> · <strong>{blooms}</strong> in bloom <span className="muted small">(solved with no nudges)</span></>}
            {koi > 0 && <> · <strong>{koi}</strong> koi <span className="muted small">(one for every 3 days in a row)</span></>}
          </span>
        )}
      </figcaption>
    </figure>
  );
}
